export type PlanTier = 'basic' | 'professional' | 'enterprise'
export type BillingPeriod = 'monthly' | 'yearly'

const billingPeriodAliases: Record<string, BillingPeriod> = {
  monthly: 'monthly',
  month: 'monthly',
  mensual: 'monthly',
  yearly: 'yearly',
  year: 'yearly',
  annual: 'yearly',
  anual: 'yearly',
}

const BILLING_PERIOD_PARAM_KEYS = [
  'period',
  'billing',
  'billingPeriod',
  'billing_period',
  'billing-period',
] as const

export class BillingError extends Error {
  code: string

  constructor(message: string, code: string) {
    super(message)
    this.code = code
    this.name = 'BillingError'
  }
}

type CheckoutOptions = {
  newTab?: boolean
  billingPeriod?: BillingPeriod
}

const planAliases: Record<string, PlanTier> = {
  starter: 'basic',
  pro: 'professional',
  growth: 'professional',
  enterprise: 'enterprise',
  basic: 'basic',
  professional: 'professional',
}

export function normalizePlanTier(plan: PlanTier | string): PlanTier {
  const key = plan.toString().toLowerCase()
  return planAliases[key] ?? 'basic'
}

export function normalizeBillingPeriod(
  period?: BillingPeriod | string | null
): BillingPeriod | null {
  if (!period) return null
  const key = period.toString().trim().toLowerCase()
  return billingPeriodAliases[key] ?? null
}

type SearchParamsLike = {
  get: (key: string) => string | null
}

export function resolveBillingPeriodFromSearchParams(
  searchParams?: SearchParamsLike | null
): BillingPeriod | null {
  if (!searchParams) return null
  for (const key of BILLING_PERIOD_PARAM_KEYS) {
    const value = searchParams.get(key)
    const normalized = normalizeBillingPeriod(value)
    if (normalized) return normalized
  }
  return null
}

export function extractBillingPeriodFromLocation(): BillingPeriod | null {
  if (typeof window === 'undefined') return null
  try {
    const current = new URL(window.location.href)
    return resolveBillingPeriodFromSearchParams(current.searchParams)
  } catch {
    return null
  }
}

export async function redirectToCheckout(
  plan: PlanTier,
  options: CheckoutOptions = {}
): Promise<void> {
  const params = new URLSearchParams()
  params.set('plan', plan)
  const normalizedPeriod = normalizeBillingPeriod(options.billingPeriod)
  if (normalizedPeriod) {
    params.set('period', normalizedPeriod)
  }
  const url = `/checkout?${params.toString()}`

  if (typeof window === 'undefined') return

  if (options.newTab) {
    window.open(url, '_blank', 'noreferrer noopener')
  } else {
    window.location.assign(url)
  }
}
